import React, { useMemo, useState } from 'react';
import { CheckCircle2, ChevronDown, Flag } from 'lucide-react';
import type { CRMUser, ProjectTask } from '../../types';
import type { GroupMemberTaskRow } from '../../utils/groupComments';
import {
  ACTION_REQUIRED_EXTERNAL_SENTINEL,
  getActionRequiredShortLabel,
  isActionRequiredExternal,
} from '../../utils/actionRequiredDisplay';
import './GroupCommentActionRequiredStrip.css';

const PICK_EXTERNAL_VALUE = '__pick_external__';

export interface GroupCommentActionRequiredStripProps {
  tasks: GroupMemberTaskRow[];
  teamMembers: Pick<CRMUser, 'uid' | 'displayName' | 'role' | 'company'>[];
  currentUserId: string;
  onUpdateTask: (taskId: string, updates: Partial<ProjectTask>) => void | Promise<void>;
  /** Opens the mark-complete date picker (CompleteTaskActualsModal) for this task. */
  onCompleteTask?: (taskId: string) => void;
};

function arSelectValue(task: GroupMemberTaskRow): string {
  if (!task.actionRequired) return '';
  if (isActionRequiredExternal(task.actionRequired)) return ACTION_REQUIRED_EXTERNAL_SENTINEL;
  return task.actionRequired;
}

function arSelectLabel(task: GroupMemberTaskRow, teamMembers: Pick<CRMUser, 'uid' | 'displayName'>[], currentUserId: string): string {
  if (!task.actionRequired) return 'Not set';
  if (task.actionRequired === currentUserId) return 'You';
  if (isActionRequiredExternal(task.actionRequired)) {
    const note = (task.actionRequiredNote || '').trim();
    return note ? `External: ${note}` : 'External';
  }
  const member = teamMembers.find((u) => u.uid === task.actionRequired);
  return member?.displayName || getActionRequiredShortLabel(task.actionRequired, task.actionRequiredNote, teamMembers);
}

const GroupCommentActionRequiredStrip: React.FC<GroupCommentActionRequiredStripProps> = ({
  tasks,
  teamMembers,
  currentUserId,
  onUpdateTask,
  onCompleteTask,
}) => {
  const [externalEdit, setExternalEdit] = useState<{ taskId: string; draft: string } | null>(null);

  const openTasks = useMemo(
    () => tasks.filter((t) => t.status !== 'completed'),
    [tasks],
  );

  if (!openTasks.length) return null;

  const handleArSelectChange = (task: GroupMemberTaskRow, value: string) => {
    if (value === '') {
      setExternalEdit(null);
      void onUpdateTask(task.taskId, {
        actionRequired: undefined,
        actionRequiredNote: undefined,
      } as Partial<ProjectTask>);
      return;
    }
    if (value === PICK_EXTERNAL_VALUE) {
      setExternalEdit({
        taskId: task.taskId,
        draft:
          task.actionRequired && isActionRequiredExternal(task.actionRequired)
            ? (task.actionRequiredNote || '').trim()
            : '',
      });
      return;
    }
    setExternalEdit(null);
    void onUpdateTask(task.taskId, {
      actionRequired: value,
      actionRequiredNote: undefined,
    });
  };

  const saveExternal = (taskId: string, draft: string) => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    void onUpdateTask(taskId, {
      actionRequired: ACTION_REQUIRED_EXTERNAL_SENTINEL,
      actionRequiredNote: trimmed,
    });
    setExternalEdit(null);
  };

  return (
    <div className="gc-ar-strip" data-tour="group-comment-action-required">
      <div className="gc-ar-strip-table-wrap">
        <table className="gc-ar-table">
          <thead>
            <tr>
              <th scope="col">Task</th>
              <th scope="col">Action required</th>
              <th scope="col" className="gc-ar-table-th-close">
                <span className="gc-ar-sr-only">Close task</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {openTasks.map((task) => {
              const isYou = task.actionRequired === currentUserId;
              const isExternal = isActionRequiredExternal(task.actionRequired);
              const showingExternalForm = externalEdit?.taskId === task.taskId;

              return (
                <tr key={task.taskId} className={isYou ? 'gc-ar-table-row--you' : undefined}>
                  <td className="gc-ar-table-task">
                    <span className="gc-ar-table-task-title" title={task.title}>
                      {task.title}
                    </span>
                    {task.phaseLabel ? (
                      <span className="gc-ar-strip-phase">{task.phaseLabel}</span>
                    ) : null}
                  </td>
                  <td className="gc-ar-table-ar">
                    {showingExternalForm ? (
                      <div className="gc-ar-external-form">
                        <input
                          className="gc-ar-external-input"
                          value={externalEdit.draft}
                          onChange={(e) =>
                            setExternalEdit({ taskId: task.taskId, draft: e.target.value })
                          }
                          placeholder="Who we're waiting on"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveExternal(task.taskId, externalEdit.draft);
                            if (e.key === 'Escape') setExternalEdit(null);
                          }}
                        />
                        <button
                          type="button"
                          className="gc-ar-external-save"
                          disabled={!externalEdit.draft.trim()}
                          onClick={() => saveExternal(task.taskId, externalEdit.draft)}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="gc-ar-external-cancel"
                          onClick={() => setExternalEdit(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div
                        className={`gc-ar-select-wrap${isYou ? ' gc-ar-select-wrap--you' : ''}${isExternal ? ' gc-ar-select-wrap--external' : ''}`}
                      >
                        <select
                          className="gc-ar-select"
                          value={arSelectValue(task)}
                          aria-label={`Action required for ${task.title}`}
                          title={arSelectLabel(task, teamMembers, currentUserId)}
                          onChange={(e) => handleArSelectChange(task, e.target.value)}
                        >
                          <option value="">— Not set —</option>
                          {teamMembers.map((u) => (
                            <option key={u.uid} value={u.uid}>
                              {u.uid === currentUserId
                                ? 'You'
                                : u.role === 'supplier' && u.company?.trim()
                                  ? `${u.displayName} · ${u.company.trim()}`
                                  : u.displayName}
                            </option>
                          ))}
                          {isExternal ? (
                            <option value={ACTION_REQUIRED_EXTERNAL_SENTINEL}>
                              {arSelectLabel(task, teamMembers, currentUserId)}
                            </option>
                          ) : null}
                          <option value={PICK_EXTERNAL_VALUE}>External…</option>
                        </select>
                        <ChevronDown size={14} className="gc-ar-select-chevron" aria-hidden />
                        {task.actionRequired ? (
                          <Flag
                            size={11}
                            className={`gc-ar-select-flag${isExternal ? ' gc-ar-select-flag--external' : ''}`}
                            aria-hidden
                          />
                        ) : null}
                      </div>
                    )}
                  </td>
                  <td className="gc-ar-table-close">
                    {onCompleteTask ? (
                      <button
                        type="button"
                        className="gc-ar-strip-close"
                        title="Close task — set actual dates"
                        aria-label={`Close task: ${task.title}`}
                        onClick={() => onCompleteTask(task.taskId)}
                      >
                        <CheckCircle2 size={13} aria-hidden />
                        <span>Close</span>
                      </button>
                    ) : (
                      <span className="gc-ar-table-close-empty" aria-hidden>
                        —
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GroupCommentActionRequiredStrip;
